import { BrandRepository } from '../../domain/repositories/brand.repository';
import { BrandEntity, BrandProps } from '../../domain/entities/brand.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';
import { resolveMediaUrl } from '../utils/media-url.helper';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class HttpBrandRepository implements BrandRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  private mapRawBrand(b: any): BrandEntity {
    const rawLogo = b.imageUrl || b.logoUrl || b.image?.url || null;
    return new BrandEntity({
      id: b.id,
      name: b.name,
      slug: b.slug,
      logoUrl: rawLogo ? resolveMediaUrl(rawLogo) : null,
      websiteUrl: b.websiteUrl || null,
      displayOrder: b.displayOrder ?? 0,
      isFeatured: b.isFeatured ?? false,
    });
  }

  public async getBrands(): Promise<BrandEntity[]> {
    const res = await this.httpClient.get<ApiResponse<any[]>>('/brands');
    if (!res.success || !res.data) {
      return [];
    }
    return res.data.map((b) => this.mapRawBrand(b));
  }

  public async createBrand(data: Partial<BrandProps> & { name: string }): Promise<BrandEntity> {
    const finalImageUrl = data.logoUrl?.trim() || (data as any).imageUrl?.trim() || '/placeholder-brand.png';
    const payload = {
      name: data.name.trim(),
      slug: data.slug?.trim() || undefined,
      imageUrl: finalImageUrl,
      status: true,
    };

    const res = await this.httpClient.post<ApiResponse<any>>('/brands', payload);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al registrar la marca oficial');
    }
    return this.mapRawBrand(res.data);
  }

  public async updateBrand(id: string, data: Partial<BrandProps>): Promise<BrandEntity> {
    const payload: Record<string, any> = {
      name: data.name?.trim(),
      slug: data.slug?.trim() || undefined,
    };
    if (data.logoUrl?.trim() || (data as any).imageUrl?.trim()) {
      payload.imageUrl = (data.logoUrl?.trim() || (data as any).imageUrl?.trim());
    }

    const res = await this.httpClient.put<ApiResponse<any>>(
      `/brands/${encodeURIComponent(id)}`,
      payload
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar la marca oficial');
    }
    return this.mapRawBrand(res.data);
  }

  public async deleteBrand(id: string): Promise<void> {
    const res = await this.httpClient.delete<ApiResponse<null>>(`/brands/${encodeURIComponent(id)}`);
    if (!res.success) {
      throw new Error(res.message || 'Error al eliminar la marca');
    }
  }
}
